import { getImage, GatsbyImage } from 'gatsby-plugin-image'
import { graphql, useStaticQuery } from 'gatsby'
import { formattedDate } from 'utils/dateutils'
import React from 'react'
import RightColWide from '../RightColWide'
import Link from 'components/Link'
import slugify from 'slugify'
import CallToAction from '../CallToAction'
import { useI18next } from 'gatsby-plugin-react-i18next'

interface Category {
    fieldValue: string
    totalCount: number
}
const LatestBlogComponent = ({ latestEnglishBlogPost, latestArabicBlogPost, item }) => {
    const { language } = useI18next()
    const latestBlogPost = language === 'en' ? latestEnglishBlogPost?.nodes[0] : latestArabicBlogPost?.nodes[0]
    const featuredImage = latestBlogPost?.frontmatter?.featuredImage
    const parent = latestBlogPost?.parent
    const date = latestBlogPost?.frontmatter?.date
    const blogTitle = latestBlogPost?.frontmatter?.title
    const slug = latestBlogPost?.fields?.slug
    const featuredImg = featuredImage || parent?.cover?.image
    const image = featuredImg && getImage(featuredImg)
    return (
        <Link
            className="relative mt-2 inline-block text-inherit hover:text-inherit active:top-[1px] active:scale-[.99]"
            to={slug}
        >
            {image && (
                <GatsbyImage alt="Blog featured image" image={image} className="pointer-events-none rounded-sm" />
            )}
            <h4 className="m-0 mt-2 text-lg  font-bold leading-tight">{blogTitle}</h4>
            <p className="m-0 mt-1 text-sm  dark:text-white/50">{formattedDate(date)}</p>
        </Link>
    )
}
const CategoriesComponent = ({ group, item, no }) => {
    const { language } = useI18next()
    const filteredCategories = group.filter((category) => {
        return category.edges.some((edge) => edge.node.frontmatter.locale === language)
    })
    const categories = filteredCategories.sort((a: Category, b: Category) => {
        return b?.totalCount - a?.totalCount
    })
    return (
        <>
            <h4 className="mt-4 mb-1 text-[15px] font-semibold opacity-40">{item.title}</h4>
            <ul className="m-0 list-none p-0">
                {categories.slice(0, no).map(({ fieldValue }: Category, index) => {
                    const slug = slugify(fieldValue, { lower: true })
                    const url = `${item.urlTemplate}${slug}`
                    return (
                        <li key={index} className="group">
                            <Link
                                to={url}
                                className="group block border-b border-dashed border-gray-accent-light py-2.5 group-last:border-b-0"
                            >
                                <h3 className="m-0 text-sm opacity-70 transition-all group-hover:text-red group-hover:opacity-100">
                                    {fieldValue}
                                </h3>
                            </Link>
                        </li>
                    )
                })}
            </ul>
        </>
    )
}
interface Links {
    title: string
    url: string
    description: string
}

export default function Blog({ linkListItem }) {
    const { title, subtitle, items } = linkListItem
    let cta = linkListItem.cta
    if (Array.isArray(cta) && cta?.length == 1) {
        cta = cta[0]
    }
    const {
        latestArabicBlogPost,
        latestEnglishBlogPost,
        categories: { group },
    } = useStaticQuery(query)
    return (
        <RightColWide>
            <div className="flex h-full flex-col">
                <div className="px-3">
                    <h2 className="m-0 mb-2 text-[18px] font-bold text-black opacity-70">{title}</h2>
                    {items?.length > 0 &&
                        items.map((item, index) => {
                            if (
                                (item.enable === false || item.enable?.toLowerCase?.() === 'false') &&
                                !process.env.RENDER_DISABLED_COMPONENTS
                            ) {
                                return <></>
                            }
                            return (
                                <>
                                    {item.type == 'LatestBlogComponent' && (
                                        <LatestBlogComponent
                                            key={index}
                                            latestEnglishBlogPost={latestEnglishBlogPost}
                                            latestArabicBlogPost={latestArabicBlogPost}
                                            item={item}
                                        />
                                    )}
                                    {item.type == 'CategoriesComponent' && (
                                        <CategoriesComponent
                                            key={index}
                                            group={group}
                                            item={item}
                                            no={item.categoryNumbers || 5}
                                        />
                                    )}
                                </>
                            )
                        })}
                    <CallToAction to={cta?.url} className="mt-2 mb-8 !w-full">
                        {cta?.title}
                    </CallToAction>
                </div>
            </div>
        </RightColWide>
    )
}

const query = graphql`
    {
        latestEnglishBlogPost: allMdx(
            filter: {
                isFuture: { eq: false }
                fields: { slug: { regex: "/^/blog/" } }
                frontmatter: { date: { ne: null }, locale: { eq: "en" } }
            }
            sort: { order: DESC, fields: frontmatter___date }
            limit: 1
        ) {
            nodes {
                id
                fields {
                    slug
                }
                frontmatter {
                    category
                    date
                    title
                    locale
                    featuredImage {
                        childImageSharp {
                            gatsbyImageData(width: 350)
                        }
                    }
                }
                parent {
                    ... on GoogleDocs {
                        cover {
                            image {
                                childImageSharp {
                                    gatsbyImageData(width: 350)
                                }
                            }
                        }
                    }
                }
            }
        }
        latestArabicBlogPost: allMdx(
            filter: {
                isFuture: { eq: false }
                fields: { slug: { regex: "/^/blog/" } }
                frontmatter: { date: { ne: null }, locale: { eq: "ar" } }
            }
            sort: { order: DESC, fields: frontmatter___date }
            limit: 1
        ) {
            nodes {
                id
                fields {
                    slug
                }
                frontmatter {
                    category
                    date
                    title
                    locale
                    featuredImage {
                        childImageSharp {
                            gatsbyImageData(width: 350)
                        }
                    }
                }
                parent {
                    ... on GoogleDocs {
                        cover {
                            image {
                                childImageSharp {
                                    gatsbyImageData(width: 350)
                                }
                            }
                        }
                    }
                }
            }
        }
        categories: allMdx(
            filter: {
                isFuture: { eq: false }
                fields: { slug: { regex: "/^/blog/" } }
                frontmatter: { date: { ne: null } }
            }
        ) {
            group(field: frontmatter___category) {
                fieldValue
                totalCount
                edges {
                    node {
                        frontmatter {
                            locale
                        }
                    }
                }
            }
        }
    }
`
